import * as React from "react";
import * as moment from "moment";
import {
    CustomProperty,
    FalconNavigatorHeader,
    ICacheManager,
    ICustomerPortalService,
    IncludedProductsEnum,
    Party,
    Policy,
    productHelper,
    ProductsEnum,
    Quote,
    resourcesHelper,
    Subscription,
    Types,
    User
} from "@falcon/services";
import {authorize, Overlay} from "@athena-portal/components";
import {lazyInject} from "@athena-portal/core";
import {PluginTemplates} from "../../../utils/PluginTemplates";
import FalconHeader from "../../../components/navigation/FalconHeader";
import FalconFooter from "../../../components/navigation/FalconFooter";
import {PaymentMessageBanner} from "../../../components/banners/PaymentMessageBanner";
import {ImgTileLink} from "../../../components/ImgTileLink";
import {SubscriptionEndBanner} from "../../../components/banners/SubscriptionEndBanner";
import {ClaimSubmissionBanner} from "../../../components/claims/ClaimSubmissionBanner";
import {StorageSuccessModal} from "../../../components/modals/StorageSuccessModal";
import {getAmountToDisplay} from "../../../CommonFunctions";
import {MaintenanceBannerWidget} from "@falcon/plugin-falcon-journey";
import {BackButton} from "../../../components/navigation/BackButton";
import {ButtonLink} from "../../../components/navigation/ButtonLink";
import {
    EditCarMtaSuccessModalWordingGenerator
} from "../annual/motor/yourCarsPage/modal/EditCarMtaSuccessModalWordingGenerator";
import {CoronaBanner} from "../../../components/banners/CoronaBanner";

interface IState {
    subscriptionPolicies?: Policy[];
    allPolicies?: Policy[];
    subscription? : Subscription;
    party?: Party;
    quotes?: Quote[];
    user?: User;
    userLoaded: boolean;
    quotesLoaded: boolean;
    claimSubmissionDate?: string;
    showClaimsButton: boolean;
    oldMissedPaymentProcess?: boolean;
    submittedClaimNumber?: string;
    hasIdp: boolean;
}

/*
 * This class contains a lot of subscription-related code which is now phased out;
 * for Wolf policies it is used for no-policy dashboard (the one when you register but the policy is not bought yet)
 */
@authorize
export class DashboardPage extends React.Component<{}, IState> {

    private pluginTemplates = new PluginTemplates();
    private referrerJourney: string | null;
    private showSuccessPopup: string | null;
    private successPopupTitle: string;
    private modalContent: React.ReactNode;

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;
    @lazyInject(Types.CacheManager)
    private cacheManager: ICacheManager;

    constructor(props: {}) {
        super(props);

        this.state = {
            quotesLoaded: false,
            showClaimsButton: false,
            userLoaded: false,
            hasIdp: false,
        };

        this.loadProfile = this.loadProfile.bind(this);
    }

    public componentDidMount() {
        this.pluginTemplates.setPageTitle("Dashboard");

        if (window.sessionStorage.getItem("FalconCache_getUserById") !== null) {
            this.cacheManager.clearCache("FalconCache_getUserById");
        }
        window.sessionStorage.setItem("fromDashboard", "true");

        this.customerPortalService.getUserPoliciesWithLatestVersions().subscribe((policies: Policy[]) => {
            this.setState({
                allPolicies: policies,
                subscriptionPolicies: policies.filter((policy: Policy) => !policy.products.includes(ProductsEnum.WOLF_MOTOR))
            });

            if (policies && policies.length > 0) {
                this.customerPortalService.getSubscription().subscribe((subscription: Subscription) => {
                    this.setState({
                        subscription
                    });
                });

                this.customerPortalService.getProfile().subscribe((party: Party) => {
                    this.loadProfile(party);
                });
            } else {
                this.customerPortalService.hasIdp().subscribe((hasIdp: boolean) => {
                    this.setState({
                        hasIdp
                    });
                });
            }
        });

        this.customerPortalService.getUserById().subscribe((user: User) => {
            this.setState({
                user,
                userLoaded: true
            });
        });

        this.customerPortalService.getQuotes().subscribe(
            (quotes: Quote[]) => {
                this.setState({
                    quotes,
                    quotesLoaded: true
                })
            }
        );
    }

    public render() {
        this.referrerJourney = window.sessionStorage.getItem("referrerJourney");
        this.showSuccessPopup = window.sessionStorage.getItem("showPopup");

        if (this.referrerJourney === "mtaReinstateSubscription") {
            this.setReactivateSubscriptionPopup();
        } else if (this.referrerJourney === "changeAddress") {
            this.setChangeAddressPopup();
        } else {
            window.sessionStorage.removeItem("showPopup");
            window.sessionStorage.removeItem("referrerJourney");
        }

        const {subscriptionPolicies: policies, subscription, party, quotesLoaded, userLoaded} = this.state;
        const dataLoaded = policies && ((policies.length === 0 && userLoaded) || party && subscription && quotesLoaded);
        let onlyMonthlySubscriptionPolicyPresent: boolean = true;
        let hasMultiplePolicies: boolean = false;

        if (policies && policies.length > 1) {
            hasMultiplePolicies = true;
        }

        if (policies && policies.length > 0) {
            onlyMonthlySubscriptionPolicyPresent = (this.state.subscription?.allPoliciesAreCancelled &&
                    productHelper.includesOnlyMonthlySubscriptionProduct(this.state.allPolicies!)) ||
                        productHelper.includesOnlyNonExpireMonthlySubscriptionProduct(this.state.allPolicies!);
        }

        return (
            <React.Fragment>
                <Overlay show={!dataLoaded}/>
                {this.showSuccessPopup && <StorageSuccessModal title={this.successPopupTitle} explanationWording={this.modalContent} anythingElseTitle={EditCarMtaSuccessModalWordingGenerator.generateAnythingElseChangedModalTitle()} explanationAnythingElseWording={EditCarMtaSuccessModalWordingGenerator.generateAnythingElseChangedModalBody()}/>}
                <FalconHeader/>
                <div className="page-contents dashboard-page">
                    <FalconNavigatorHeader
                        {...(!this.state.subscription ? {} : { back: "/dashboard" })}
                        title={{
                            label: `Hi ${party?.firstName}`,
                            hidden: !party
                        }}
                    />
                    {dataLoaded && this.renderPageContents(onlyMonthlySubscriptionPolicyPresent)}
                    {(hasMultiplePolicies || !onlyMonthlySubscriptionPolicyPresent) && <BackButton isTop={false} link={"/dashboard/"}/>}
                </div>
                <FalconFooter/>
            </React.Fragment>
        )
    }

    private renderPageContents(onlyMonthlySubscriptionPolicyPresent: boolean): React.ReactNode {
        if (!this.state.subscription) {
            return this.renderNewClientPageContents();
        } else {
            return (
                <React.Fragment>
                    {this.renderBanners(onlyMonthlySubscriptionPolicyPresent)}
                    {this.renderLinkGrid(onlyMonthlySubscriptionPolicyPresent)}
                </React.Fragment>
            );
        }
    }

    private renderBanners(onlyMonthlySubscriptionPolicyPresent: boolean): React.ReactNode {
        const {subscription, claimSubmissionDate, oldMissedPaymentProcess, submittedClaimNumber} = this.state;
        const showSubscriptionBanner = !subscription!.cancelledBeforeStart && subscription!.allPoliciesAreCancelled &&
            !(subscription!.renewalSecondPaymentFailed && moment().isBefore(subscription!.currentPeriodEndDate));
        const showClaimBanner = claimSubmissionDate && moment(claimSubmissionDate).add(48, "hours").isAfter(moment());

        return (
            <React.Fragment>
                <MaintenanceBannerWidget product={ProductsEnum.SUBSCRIPTION}/>
                {onlyMonthlySubscriptionPolicyPresent && <CoronaBanner/>}
                {showSubscriptionBanner && <SubscriptionEndBanner subscription={subscription!}/>}
                {!showSubscriptionBanner && !showClaimBanner && <PaymentMessageBanner showInfoModal={true} oldMissedPaymentProcess={oldMissedPaymentProcess} subscription={subscription!}/>}
                {!showSubscriptionBanner && showClaimBanner && <ClaimSubmissionBanner claimSubmissionDate={claimSubmissionDate!} submittedClaimNumber={submittedClaimNumber}/>}
            </React.Fragment>
        );
    }

    private renderNewClientPageContents(): React.ReactNode {
        return (
            <React.Fragment>
                <div className="banner wide-gutters banner-info">
                    <div className="banner-text">
                        {this.state.hasIdp ? (
                            <div className="flex-column">
                                <p>Heads up - it doesn't look like there's a policy linked to this account.</p><br/>
                                <p>
                                    <b>
                                        If you already have an {resourcesHelper.THEME} Insurance policy, please log in with the email
                                        address and password you used when you bought the policy.
                                    </b>
                                </p>
                            </div>
                        ) : (
                            "While you do not have an active insurance cover with us, we will keep your details safe and ready for when you need us."
                        )}
                    </div>
                </div>
                <div className="new-client-panel">
                    <img src="/img/Flow-empty.svg" alt="Emptiness"/>
                    <div className="new-client-panel-heading">It's empty around here...</div>
                    <ButtonLink id="quote" primary={true} to="/dashboard/viewOrGetQuotes">
                        Get a quote
                    </ButtonLink>
                </div>
                <div className="link-grid">
                    <div className="d-flex flex-wrap single-tile">
                        <div className={resourcesHelper.THEME === "Flow" ? "col-6" : "flexItem quotes-old single-tile"}>
                            <ImgTileLink src={`/${resourcesHelper.THEME}/img/quotes-icon.png`} alt="Quotes"
                                         to="/dashboard/viewOrGetQuotes"
                                         {...this.props}>
                                Your quote(s)
                            </ImgTileLink>
                        </div>
                    </div>
                </div>
            </React.Fragment>
        );
    }

    private loadProfile(party: Party): void {
        if (party.customProperties !== undefined) {
            const properties: CustomProperty[] = party.customProperties;
            for (const property of properties) {
                if (property.key === "customerClaimSubmissionDate") {
                    this.setState({
                        claimSubmissionDate: property.value
                    });
                } else if (property.key === "claimCenterClaimSubmitted") {
                    this.setState({showClaimsButton: property.value === 'true'})
                } else if (property.key === "claimCenterClaimNumber") {
                    this.setState({submittedClaimNumber: property.value})
                } else if (property.key === "oldMissedPaymentProcess") {
                    this.setState({
                        oldMissedPaymentProcess: property.value === 'true'
                    });
                }
            }
        }
        this.setState({party});
    }

    private renderLinkGrid(onlyMonthlySubscriptionPolicyPresent: boolean): React.ReactNode {
        const pendingPayment: boolean = this.state.subscription!.nextPaymentAmount != null;
        const renderMotorTiles: boolean = (this.state.subscription?.allPoliciesAreCancelled && productHelper.includesMotorProduct(this.state.subscriptionPolicies!)) ||
            productHelper.includesNonExpiredMotorPolicies(this.state.subscriptionPolicies!);
        const renderRentersTiles: boolean = (this.state.subscription?.allPoliciesAreCancelled && productHelper.includesRentersProduct(this.state.subscriptionPolicies!)) ||
            productHelper.includesNonExpiredRentersPolicies(this.state.subscriptionPolicies!);
        const showQuotesTile: boolean = this.state.quotes! && this.state.quotes!.length > 0;
        const showCancelSubscriptionTile: boolean = !this.state.subscription?.allPoliciesAreCancelled;

        return (
            <div className="link-grid">
                <div className="d-flex flex-wrap">
                    {renderMotorTiles && (
                        <>
                            <div className="flexItem">
                                <ImgTileLink src="/img/policy-summary.png" alt="Car cover" to="/dashboard/yourCarCovers"
                                             {...this.props}>
                                    Your car cover(s)
                                </ImgTileLink>
                            </div>
                            <div className="flexItem">
                                <ImgTileLink src="/img/wolf-your-car.png" alt="Car" to="/dashboard/yourCars"
                                             {...this.props}>
                                    Your car(s)
                                </ImgTileLink>
                            </div>
                        </>
                    )}
                    {renderRentersTiles && (
                        <>
                            <div className="flexItem">
                                <ImgTileLink src="/img/policy-summary.png" alt="Renters cover"
                                             to="/dashboard/yourRentersCovers"
                                             {...this.props}>
                                    Your renters cover(s)
                                </ImgTileLink>
                            </div>
                            <div className="flexItem">
                                <ImgTileLink src="/img/specify-items-icon.png" alt="Specified items"
                                             to="/dashboard/valuables"
                                             {...this.props}>
                                    Specified items
                                </ImgTileLink>
                            </div>
                        </>
                    )}
                    {this.renderClaimsSection()}
                    <div className="flexItem">
                        <ImgTileLink src="/img/payment-card-icon.png" alt="Payment" to="/dashboard/paymentHistory"
                                     notification={pendingPayment}
                                     {...this.props}>
                            Payment details
                        </ImgTileLink>
                    </div>
                    {onlyMonthlySubscriptionPolicyPresent && (
                        <div className="flexItem">
                            <ImgTileLink src="/img/account-details.png" alt="Account details" to="/dashboard/account"
                                         {...this.props}>
                                Account details
                            </ImgTileLink>
                        </div>
                    )}
                    <div className="flexItem">
                        <ImgTileLink src="/img/documents-icon.png" alt="Documents" to="/dashboard/documents"
                                     {...this.props}>
                            Your documents
                        </ImgTileLink>
                    </div>
                    {showQuotesTile &&
                        <div className="flexItem">
                            <ImgTileLink src="/img/quotes-icon.png" alt="Quotes" to="/dashboard/quotes"
                                         {...this.props}>
                                Your quote(s)
                            </ImgTileLink>
                        </div>
                    }
                    {showCancelSubscriptionTile &&
                        <div className="flexItem">
                            <ImgTileLink src="/img/cancel-policy-icon.png" alt="Cancel subscription"
                                         to="/journey/show/product/FlowSubscription/process/mtc"
                                         {...this.props}>
                                Cancel subscription
                            </ImgTileLink>
                        </div>
                    }
                </div>
            </div>
        )
    }

    private claimsLink(): string {
        const includedProductsEnum: IncludedProductsEnum = productHelper.getIncludedProducts(this.state.subscriptionPolicies!);
        if (this.state.subscriptionPolicies!.length > 1){
            return "/dashboard/selectCoverage"
        } else if (includedProductsEnum === IncludedProductsEnum.MOTOR){
            return "/dashboard/windscreen";
        } else if (includedProductsEnum === IncludedProductsEnum.RENTERS){
            return "/dashboard/rentersClaims/new";
        }
        throw Error("Unsupported product: " + includedProductsEnum);
    }

    private setReactivateSubscriptionPopup = (): void => {
        if (this.state.subscriptionPolicies) {
            const additionalPremium: string | null = window.sessionStorage.getItem("additionalPremium");

            if (additionalPremium && additionalPremium !== "0") {
                this.successPopupTitle = "Payment successful";
                this.modalContent =
                    <p>
                        We’ve saved your changes, and <b>{getAmountToDisplay(additionalPremium)}</b> has been debited from your card{' '}
                        for <strong>reactivating subscription</strong>
                    </p>
            } else {
                this.successPopupTitle = "All done";
                this.modalContent =
                    <p>We’ve saved your changes, and there’s no extra cost for reactivating subscription</p>
            }
        }
    };

    private setChangeAddressPopup = (): void => {

        const returnPremium: string | null = window.sessionStorage.getItem("returnPremium");
        const additionalPremium: string | null = window.sessionStorage.getItem("additionalPremium");

        if (returnPremium && returnPremium !== "0") {
            this.successPopupTitle = "Your refund is on the way";
            this.modalContent =
                <p>
                    We’ve saved your changes, and we’ll refund you <b>{getAmountToDisplay(returnPremium)}</b> for changing your address.
                    Please allow two days for it to arrive.
                </p>;
        } else if (additionalPremium && additionalPremium !== "0") {
            this.successPopupTitle = "Payment successful";
            this.modalContent =
                <p>
                    We’ve saved your new address and <b>{getAmountToDisplay(additionalPremium)}</b> has been debited from your card.
                </p>
        } else {
            this.setAllDonePopup();
        }
    };

    private renderClaimsSection(): React.ReactNode {
        const renderClaimsListButton: boolean = this.state.showClaimsButton;

        return renderClaimsListButton ?
            (
                <div className="flexItem">
                    <ImgTileLink src="/img/Flow-make-a-claim.svg" alt="Claims" to="/dashboard/rentersClaims/"
                                 {...this.props}>
                        Claims
                    </ImgTileLink>
                </div>
            ) :
        (
            <div className="flexItem">
                <ImgTileLink src="/img/Flow-make-a-claim.svg" alt="Claim" to={this.claimsLink()}
                             {...this.props}>
                    Make a claim
                </ImgTileLink>
            </div>
        )
    }

    private setAllDonePopup = () : void => {
        this.successPopupTitle = "All done";
        this.modalContent =
            <p>
                We’ve saved your changes.
            </p>;
    };
}
